import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { SunSnow, RotateCcw, Zap } from "lucide-react";

export const SeasonalRatesPanel = ({ onChanged }) => {
  const [state, setState] = useState(null);
  const [name, setName] = useState("");
  const [percent, setPercent] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/seasonal-rates").then((res) => setState(res.data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const apply = async () => {
    if (!name.trim() || percent === "") {
      toast.error("Enter a season name and a percentage (e.g. Summer, 15 or -10).");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post("/admin/seasonal-rates/apply", { name: name.trim(), percent: Number(percent) });
      setState(res.data);
      toast.success(`${res.data.name} rates applied — all room prices updated on the website.`);
      setName("");
      setPercent("");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not apply seasonal rate"));
    } finally {
      setBusy(false);
    }
  };

  const revert = async () => {
    setBusy(true);
    try {
      await api.post("/admin/seasonal-rates/revert");
      setState({ active: false });
      toast.success("Seasonal rate removed — all room prices restored.");
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not revert"));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "bg-white border border-[#0D1B2A]/20 focus:border-[#C5A059] focus:ring-2 focus:ring-[#C5A059]/30 rounded-xl px-4 py-2.5 text-[#0D1B2A] placeholder:text-gray-400 outline-none transition-all text-sm";

  return (
    <div className="bg-[#0A192F] rounded-2xl p-6 sm:p-8 mb-8" data-testid="seasonal-rates-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-serif-display text-xl font-semibold text-[#F8F6F0] flex items-center gap-2">
            <SunSnow size={19} className="text-[#C5A059]" /> Seasonal Rates
          </h3>
          <p className="text-[#F8F6F0]/55 text-sm mt-1.5 max-w-xl">
            Raise or lower every room price at once. Example: <span className="text-[#C5A059]">Summer · 15</span> adds 15% to all rates; <span className="text-[#C5A059]">Winter · -10</span> takes 10% off.
          </p>
        </div>
        {state?.active && (
          <div className="flex items-center gap-3" data-testid="seasonal-active-badge">
            <span className="text-xs font-semibold uppercase tracking-widest bg-[#C5A059] text-[#0A192F] rounded-full px-4 py-2">
              {state.name} · {state.percent > 0 ? "+" : ""}{state.percent}% active
            </span>
            <button
              onClick={revert}
              disabled={busy}
              className="flex items-center gap-2 text-xs font-semibold text-[#F8F6F0] border border-[#F8F6F0]/25 hover:border-[#C5A059] hover:text-[#C5A059] rounded-full px-4 py-2 transition-all disabled:opacity-60"
              data-testid="seasonal-revert-button"
            >
              <RotateCcw size={13} /> Revert to normal
            </button>
          </div>
        )}
      </div>

      {!state?.active && (
        <div className="flex flex-col sm:flex-row gap-3 mt-5 sm:items-end">
          <div className="flex-1">
            <label className="text-xs text-[#F8F6F0]/50 block mb-1.5">Season name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Summer Peak" className={`${inputCls} w-full`} data-testid="seasonal-name-input" />
          </div>
          <div className="sm:w-40">
            <label className="text-xs text-[#F8F6F0]/50 block mb-1.5">Adjustment %</label>
            <input type="number" value={percent} onChange={(e) => setPercent(e.target.value)} placeholder="15 or -10" className={`${inputCls} w-full`} data-testid="seasonal-percent-input" />
          </div>
          <button
            onClick={apply}
            disabled={busy}
            className="flex items-center justify-center gap-2 bg-[#C5A059] text-[#0A192F] hover:bg-[#D4AF37] rounded-full px-7 py-3 font-semibold text-sm transition-all disabled:opacity-60 shrink-0"
            data-testid="seasonal-apply-button"
          >
            <Zap size={15} /> {busy ? "Applying…" : "Apply to All Rooms"}
          </button>
        </div>
      )}
    </div>
  );
};
